/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 * PdpDataHandlerService create data for PDP in digitalData
 */
// tslint:disable
import { Injectable } from '@angular/core';
import {
  Cart,
  CartItem,
  CartLine,
  Promotion,
} from '@support/api/proxies/responses/api-cart-response.interface';
import {
  PdpProuct,
  PdpPromotions,
  UserDetails,
  EventDetails,
  ProductInfo,
  PdlSubtypeCartMapping,
  ClickToCallEventDetails,
} from '@support/shared/interfaces/pdl-data.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Product, ItemDetail } from '@support/api/common/product.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
// import { DIGITALDATA_CONSTANT, PDP_TYPE } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';

import { BYOD_PDL_COMPONENTS } from '@experience/shop/byod/components/byod-validation.constants';
// import { SimDisplay } from '@experience/shop/byod/interfaces/byod-landing.interface';
import {
  PDL_ASSET_TYPE,
  PDL_EVENT_ACTION,
  PDL_EVENT_TYPE,
  PDL_PATH,
} from '@experience/reb-pdl/constants/pdl.constants';
import {
  PDP_SUBTYPE_CONSTANT,
  PDL_USER_CONSTANT,
  ALERT_EVENT_CONSTANTS,
  QTY_ALLOWED_LIST,
  CART_EVENT_NAME,
} from '@support/shared/constants/pdl-data.constants';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
// import { CHANNELS, SETTINGS } from '@environment/settings';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';

@Injectable({
  providedIn: 'root',
})
export class PdpDataHandlerService {
  constructor(
    private objectService: ObjectService,
    private commonUtilService: CommonUtilService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private pdlWrapperService: PdlWrapperService,
    private pdlService: PdlService
  ) {}

  /**
   * method to create Cart.Product[]
   * @returns ProductInfo[]
   */
  public createProductInfoForCart(cartData: Cart): Array<ProductInfo> {
    let product = [];
    if (cartData?.accessories && cartData?.accessories?.length) {
      product.push(...this.mapDataForProductInfo(this.groupAccessory(cartData.accessories)));
    }
    if (cartData?.devices && cartData?.devices?.length) {
      product.push(...this.mapDataForProductInfo(cartData.devices));
    }
    if (cartData?.lines?.length) {
      cartData.lines.forEach((line: CartLine) => {
        if (line.device) {
          line.device.lineNumber = line.id;
          product.push(...this.mapDataForProductInfo([line.device]));
        }
        if (line?.plan?.length > 0) {
          line.plan[0].lineNumber = line.id;
          line.plan[0].availability = PDP_SUBTYPE_CONSTANT.AVAILABLE;
          product.push(...this.mapDataForProductInfo(line.plan, PDP_SUBTYPE_CONSTANT.PLANS));
        }
        if (line?.services?.length > 0) {
          line.services.forEach((service: CartItem) => {
            service.lineNumber = line.id;
            service.availability = PDP_SUBTYPE_CONSTANT.AVAILABLE;
          });
          product.push(...this.mapDataForProductInfo(line.services, PDP_SUBTYPE_CONSTANT.SERVICE));
        }
        if (line.simCard) {
          line.simCard.lineNumber = line.id;
          line.simCard.availability = PDP_SUBTYPE_CONSTANT.AVAILABLE;
          product.push(...this.mapDataForProductInfo([line.simCard]));
        }
      });
    }
    return product;
  }

  /**
   * method to fetch product object to map cart.products for pdl
   * @returns ProductInfo[]
   */
  private mapDataForProductInfo(items: CartItem[] = [], subTypeParam?: string): Array<ProductInfo> {
    let productInfo = [];
    items.forEach((item: CartItem) => {
      if ((item.actionCode || item.statusCode) === PDP_SUBTYPE_CONSTANT.ADD) {
        const promotions = this.objectService.get(item, 'price.promotions', []);
        const setQty = [
          ...this.objectService.get(item, 'productType', []),
          ...this.objectService.get(item, 'productSubType', []),
        ].some((item: string) => QTY_ALLOWED_LIST.includes(item));
        const subtype =
          this.setSubCategoryForCart(
            this.objectService.get(item, 'sku', ''),
            this.objectService.get(item, 'productSubType.0', '')
          ) || subTypeParam;
        const type = this.getType(subtype);
        const sim = subtype === PDP_SUBTYPE_CONSTANT.SIM ? true : false;
        const availability = this.setAvailabilityForProducts(
          item,
          sim,
          this.objectService.get(item, 'availability', ''),
          true
        );
        const isMonthly: boolean = [
          PDP_SUBTYPE_CONSTANT.PLANS,
          PDP_SUBTYPE_CONSTANT.SERVICE,
        ].includes(subtype);
        productInfo.push({
          productInfo: {
            id: this.objectService.get(item, 'sku')
              ? this.objectService.get(item, 'sku')
              : this.objectService.get(item, 'offerId'),
            name: this.objectService.get(item, 'offerName', ''),
            familyId: this.objectService.get(item, 'familyId')
              ? this.objectService.get(item, 'familyId')
              : this.objectService.get(item, 'offerId', ''),
            type: type,
            subType: subtype ? subtype : subTypeParam,
            classification: 'prepaid',
            availabilityStatus: availability,
            dueMonthly: this.objectService.get(
              item,
              'price.priceSummary.monthlyPrice.salePrice.amount',
              isMonthly ? '0' : ''
            ),
            dueToday: this.objectService.get(
              item,
              'price.priceSummary.payNowPrice.salePrice.amount',
              ''
            ),
            listRetailPrice: parseInt(this.objectService.get(item, 'price.cost.amount', '')),
            isEIP: 'false',
            qty: setQty ? this.objectService.get(item, 'quantity', '') : '',
            lineNumber: this.objectService.get(item, 'lineNumber')
              ? parseInt(this.objectService.get(item, 'lineNumber').slice(1)).toString()
              : '',
            finType: 'FRP',
            eipMonth: '0',
            promo: promotions.length
              ? [
                  {
                    code: this.objectService.get(promotions, '0.promoCode', ''),
                    name: this.objectService.get(promotions, '0.longDescription', ''),
                    value: this.objectService.get(promotions, '0.discountValue.amount', ''),
                    type: this.objectService.get(promotions, '0.promoType', ''),
                    subType: [],
                    credits: [],
                  },
                ]
              : [],
          },
        });
      }
    });
    return productInfo;
  }

  /**
   * method to get fetch unique products with updated quantity for digitalData.Cart
   * @returns PdpProuct
   */
  private groupAccessory(accessory: CartItem[]): Array<CartItem> {
    let uniqueAccessories = [];
    const group = this.commonUtilService.groupBy(accessory, 'offerId');
    const groupBySku = this.commonUtilService.groupBy(accessory, 'sku');
    if (groupBySku) {
      for (const [key, value] of Object.entries(groupBySku)) {
        value[0].quantity = value.length;
        uniqueAccessories.push(value[0]);
      }
    }
    return uniqueAccessories;
  }

  /**
   * method to get Product object for digitalData.Products[]
   * @returns PdpProuct
   */
  public loadProductDetailsInPDP(product: Product, sim?: boolean): PdpProuct {
    const subType = this.getSubCategory(
      this.objectService.get(product, 'productSubType', ''),
      this.objectService.get(product, 'offeringClassification.1.nameValue', '')
    );
    this.setPdlSubtypeObjectForCart(product, subType, sim);
    const type = this.getType(subType);
    const availability = this.setAvailabilityForProducts(
      product,
      sim,
      this.objectService.get(product, 'itemDetails.0.availability.availabilityStatus', '')
    );
    const data = {
      id: sim
        ? this.objectService.get(product, 'id', '')
        : this.objectService.get(product, 'itemDetails.0.id', ''),
      familyId: sim
        ? this.objectService.get(product, 'offerId', '')
        : this.objectService.get(product, 'familyId', ''),
      name: this.objectService.get(product, 'name', ''),
      type: type,
      subType: subType,
      classification: 'prepaid',
      availabilityStatus: availability,
      isEIP: 'false',
      listRetailPrice: sim
        ? this.objectService.get(
            product,
            'price.0.skuPrice.priceSummary.payNowPrice.salePrice.amount',
            ''
          )
        : this.objectService.get(
            this.objectService.get(product, 'itemDetails.0.skuPrice', []).filter((price) => {
              return (
                price.priceListLineId === 'WithoutService' &&
                price.pricingOptionName === 'FullPrice'
              );
            }),
            '0.priceSummary.payNowPrice.listPrice.amount',
            ''
          ),
      qty: '',
      finType: 'FRP',
    };
    this.setSubCategory(data.subType);
    return data;
  }
  public loadProductBackendCartInPDP(cartData: Cart, sim?: boolean): PdpProuct {
    const subType = this.getSubCategory(
      this.objectService.get(cartData, 'productSubType.0', ''),
      this.objectService.get(cartData, 'offeringClassification.1.nameValue', '')
    );
    this.setPdlSubtypeObjectForBackendCart(cartData, subType, false);
    const type = this.getType(subType);
    const availability = this.setAvailabilityForProducts(
      cartData,
      sim,
      this.objectService.get(cartData, 'availability', '')
    );
    const data = {
      id: this.objectService.get(cartData, 'id', ''),
      familyId: sim
        ? this.objectService.get(cartData, 'offerId', '')
        : this.objectService.get(cartData, 'familyId', ''),
      name: this.objectService.get(cartData, 'name', ''),
      type: type,
      subType: subType,
      classification: 'prepaid',
      availabilityStatus: availability,
      isEIP: 'false',
      listRetailPrice: this.objectService.get(
        cartData,
        'price.priceSummary.payNowPrice.listPrice.amount',
        ''
      ),
      qty: '',
      finType: 'FRP',
    };
    this.setSubCategory(data.subType);
    return data;
  }
  /**
   * method to fetch all the product Info to set product object while firing events in PDL
   * @returns ProductInfo[]
   */
  public fetchProductInfo(cartData: Cart, sku: string): ProductInfo[] {
    const products = this.createProductInfoForCart(cartData);
    var item = products.filter(function (item) {
      return item.productInfo.id === sku;
    });
    return item;
  }

  /**
   * method to get object for Promotions for PDL
   * @returns PdpPromotions[]
   */
  public getPromotions(cartData: Cart): PdpPromotions[] {
    let promo = [];
    cartData.pricing.subTotal.promotions.length > 0
      ? promo.push({
          code: this.objectService.get(cartData, 'pricing.subTotal.promotions.0.promocode', ''),
          name: this.objectService.get(
            cartData,
            'pricing.subTotal.promotions.0.longDescription',
            ''
          ),
          value: this.objectService.get(
            cartData,
            'pricing.subTotal.promotions.0.discountValue.amount',
            ''
          ),
          type: this.objectService.get(cartData, 'pricing.subTotal.promotions.0.promoType', ''),
          subType: [],
          credits: [],
        })
      : promo;
    return promo;
  }

  /**
   * method to set user related object for PDL.
   * @returns UserDetails
   */
  public setUserData(): UserDetails {
    const billingDetails = this.stateStoreUtilsService.getBillingBlockDetails();
    const storedPaymentMethods = this.stateStoreUtilsService.getStoredPaymentMethods();
    const autoPayDetails = this.stateStoreUtilsService.getAutoPayDetails();
    const paymentDetails = this.stateStoreUtilsService.getOTPModelDetails();
    const CustomerProfileInfo = this.stateStoreUtilsService.getAccountLinesInfo();
    const isCustomerLoggedIn = this.objectService.get(CustomerProfileInfo, 'customer');
    const orderId = this.stateStoreUtilsService.getCustomerOrderId();
    const data = {
      user: {
        profile: {
          account: {
            type: isCustomerLoggedIn ? PDL_USER_CONSTANT.INDIVIDUAL : '',
            subType: this.objectService.get(
              CustomerProfileInfo,
              'customer.accounts.0.accountSubType',
              ''
            ),
            userRole: this.objectService.get(
              CustomerProfileInfo,
              'customer.accounts.0.customerRole',
              ''
            ),
            category: isCustomerLoggedIn ? PDL_USER_CONSTANT.PREPAID : '',
            status: this.objectService.get(billingDetails, 'accountStatus', '').toLowerCase(),
          },
        },
        userState: isCustomerLoggedIn
          ? PDL_USER_CONSTANT.AUTHENTICATED
          : PDL_USER_CONSTANT.UNAUTHENTICATED,
        userType: isCustomerLoggedIn ? PDL_USER_CONSTANT.CUSTOMER : PDL_USER_CONSTANT.PROSPECT,
        creditClass: this.objectService.get(CustomerProfileInfo, 'customer.creditClass', ''),
        location: {
          geoPermission: '',
        },
        billing: {
          paymentAmt: orderId ? `${this.objectService.get(paymentDetails, 'total', '')}` : '',
          billDueAmt: `${this.objectService.get(autoPayDetails, 'funds.required', '')}`,
          pastDueAmt:
            this.objectService.get(billingDetails, 'availableFunds') < 0
              ? `${this.objectService.get(billingDetails, 'availableFunds')}`
              : '',
          billDueDate: `${this.objectService.get(billingDetails, 'billCycleEndDate', '')}`,
          daysUntilDue: `${this.objectService.get(billingDetails, 'remainingDays', '')}`,
          storedPayment: {
            method: this.objectService.get(
              storedPaymentMethods,
              'paymentMethods.storedPayments.0.paymentMethod',
              ''
            ),
            type: this.objectService.get(
              storedPaymentMethods,
              'paymentMethods.storedPayments.0.cardType',
              ''
            ),
            status: this.objectService.get(
              storedPaymentMethods,
              'paymentMethods.storedPayments.0.status',
              ''
            ),
            quantity: storedPaymentMethods
              ? `${this.objectService.get(storedPaymentMethods, 'paymentMethods.storedPayments', []).length}`
              : '',
            // 'default':
          },
        },
      },
    };
    return data;
  }

  /**
   * method to set EventData object for BYOD Screens
   * @returns EventDetails
   */
  public getEventData(
    eventType: string,
    name: string,
    ctaName?: string,
    dynamicContent?: string
  ): EventDetails {
    let eventData: EventDetails = {
      eventInfo: {
        eventAction: PDL_EVENT_ACTION.CLICK,
        eventType: eventType,
      },
    };
    if (name) {
      eventData.tool = {
        name: name,
      };
    }
    switch (eventType) {
      case BYOD_PDL_COMPONENTS.TOOL:
        this.objectService.set(eventData, 'tool.stage', BYOD_PDL_COMPONENTS.START);
        break;
      case BYOD_PDL_COMPONENTS.CLICKTOACTION:
        eventData.eventDescription = {
          ctaName: ctaName,
          assetType: BYOD_PDL_COMPONENTS.BUTTON,
        };
        if (name) {
          this.objectService.set(eventData, 'tool.stage', BYOD_PDL_COMPONENTS.COMPLETE);
          this.objectService.set(eventData, 'tool.dynamicContent', dynamicContent);
        }
        break;
      case BYOD_PDL_COMPONENTS.MODALLOAD:
        this.objectService.set(eventData, 'tool.stage', BYOD_PDL_COMPONENTS.COMPLETE);
        this.objectService.set(eventData, 'tool.dynamicContent', dynamicContent);
        break;
      case PDL_EVENT_TYPE.FORM_START:
      case PDL_EVENT_TYPE.FORM_COMPLETE:
        this.objectService.set(
          eventData,
          PDL_PATH.EVENT.EVENT_DESCRIPTION.FORM_NAME,
          dynamicContent
        );
        break;
      case ALERT_EVENT_CONSTANTS.PAGEALERT:
        eventData.eventInfo = {};
        eventData.alert = [
          {
            type: eventType,
            name: name,
            code: '',
          },
        ];
        break;
      case CART_EVENT_NAME.CARTREMOVE:
        delete eventData.eventInfo;
        this.objectService.set(eventData, 'category.eventCategory', CART_EVENT_NAME.CART);
        break;
      // case 'pageLoad':
      //   eventData.attribute.dynamicContent = dynamicContent;
    }
    return eventData;
  }

  /**
   * method to get subcategory for Products
   * @returns string
   */
  private getSubCategory(productSubType: string, offeringsSubType: string): string {
    const subType = this.stateStoreUtilsService.getPdlSubtypeConstants();
    const overriddenSubType = this.stateStoreUtilsService.getPdlSubtypeOverrideConstants();
    return productSubType !== PDP_SUBTYPE_CONSTANT.MOBILE_INTERNET
      ? this.objectService.get(subType, productSubType)
      : this.objectService.get(overriddenSubType, `${productSubType}.${offeringsSubType}`);
  }

  /**
   * method to map subcategory from overridden Subcategory constants
   * @returns void
   */
  private setPdlSubtypeObjectForCart(product: Product, subType: string, sim?: boolean): void {
    const items = !sim ? this.objectService.get(product, 'itemDetails', '') : [];
    let subTypeForCart = this.stateStoreUtilsService.getPdlSubtypeForCart()
      ? this.stateStoreUtilsService.getPdlSubtypeForCart()
      : [];
    if (items.length > 0 && !sim) {
      items.forEach((item: ItemDetail) => {
        const toBeInserted = { id: item.id, subType: subType };
        let prev = subTypeForCart.filter((element: PdlSubtypeCartMapping) => {
          if (element.id !== toBeInserted.id) return element;
        });
        subTypeForCart = [...prev, toBeInserted];
      });
    } else {
      subTypeForCart.push({ id: this.objectService.get(product, 'id', ''), subType: subType });
    }
    this.stateStoreUtilsService.setPdlSubtypeForCart(subTypeForCart);
  }
  public setPdlSubtypeObjectForBackendCart(product: Cart, subType: string, sim?: boolean): void {
    const items = !sim ? this.objectService.get(product, 'devices', '') : [];
    let subTypeForCart = this.stateStoreUtilsService.getPdlSubtypeForCart()
      ? this.stateStoreUtilsService.getPdlSubtypeForCart()
      : [];
    if (items.length > 0 && !sim) {
      items.forEach((item: ItemDetail) => {
        const toBeInserted = { id: item.id, subType: subType };
        let prev = subTypeForCart.filter((element: PdlSubtypeCartMapping) => {
          if (element.id !== toBeInserted.id) return element;
        });
        subTypeForCart = [...prev, toBeInserted];
      });
    } else {
      subTypeForCart.push({ id: this.objectService.get(product, 'id', ''), subType: subType });
    }
    this.stateStoreUtilsService.setPdlSubtypeForCart(subTypeForCart);
  }

  /**
   * method to update SubCategory of products for cart
   * @returns string
   */
  private setSubCategoryForCart(sku: string, productSubType: string): string {
    const subType = this.stateStoreUtilsService.getPdlSubtypeConstants();
    let subtype = this.objectService.get(subType, productSubType, '');
    if (sku) {
      const mappedSubType = this.stateStoreUtilsService.getPdlSubtypeForCart();
      if (mappedSubType) {
        mappedSubType.forEach((item: PdlSubtypeCartMapping) => {
          if (item.id === sku) {
            subtype = item.subType;
          }
        });
      }
    }
    return subtype;
  }

  /**
   * method to update Epin Info in digitalData on Price change dropdown
   * @returns void
   */
  public updateEpinInfoOnPriceChange(item: ItemDetail): void {
    const listPrice = this.objectService.get(item, 'listPrice', '');
    const id = this.objectService.get(item, 'id', '');
    this.objectService.set(window, 'digitalData.product.0.productInfo.id', id);
    this.objectService.set(window, 'digitalData.product.0.productInfo.listRetailPrice', listPrice);
  }

  private setAvailabilityForProducts(
    product: Product | CartItem,
    sim?: boolean,
    availabilityStatus?: string,
    isCart?: boolean
  ): string {
    let availability = availabilityStatus;
    const subtype = isCart
      ? this.objectService.get(product, 'productSubType.0', '')
      : this.objectService.get(product, 'productSubType', '');
    if (sim) {
      const availabilityStatusFromAPI = this.objectService.get(product, 'availability');
      availability = availabilityStatusFromAPI
        ? availabilityStatusFromAPI
        : PDP_SUBTYPE_CONSTANT.AVAILABLE;
    }
    if (subtype === PDP_SUBTYPE_CONSTANT.VOUCHER) {
      availability = PDP_SUBTYPE_CONSTANT.INSTOCK;
    }
    return availability;
  }

  /**
   * method to set SubCategory in page.pageInfo for DigitalData
   * @returns void
   */
  private setSubCategory(key: string): void {
    const subCategoryConstants = this.stateStoreUtilsService.getPageInfoSubCategoryConstantInPDP();
    if (this.objectService.get(window, 'digitalData.page.pageInfo') && subCategoryConstants) {
      this.objectService.set(
        window,
        'digitalData.page.pageInfo.subCategory',
        this.objectService.get(subCategoryConstants, key, '')
      );
    }
  }
  public setFlowName(value: string): void {
    if (this.objectService.get(window, 'digitalData.page.pageInfo')) {
      this.objectService.set(window, 'digitalData.page.pageInfo.flowName', value);
    }
  }
  public removeFlowName(): void {
    if (this.objectService.get(window, 'digitalData.page.pageInfo.flowName')) {
      this.objectService.destroy(window, 'digitalData.page.pageInfo.flowName');
    }
  }

  /**
   * method to update cart snapshot for PDL
   * @returns void
   */
  public updateCartForPDL(cart: Cart, isCartEmpty: boolean): void {
    // const isWebChannel = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB.toLowerCase();
    // if (isWebChannel) {
    if (!isCartEmpty) {
      const productInfo = this.createProductInfoForCart(cart);
      const promo = this.getPromotions(cart);
      const lineCount = this.getLineCount(productInfo);
      this.pdlWrapperService.trackData('Cart-Detail', 'cart', {
        cart: { cartData: cart, productInfo: productInfo, promo: promo, lineCount: lineCount },
      });
    } else {
      this.pdlWrapperService.trackData('Cart-Detail', 'cart', {});
    }
    // }
  }

  /**
   * method to get Removed Items from the cart for PDL
   * @returns ProductInfo[]
   */
  public getRemovedItemDetails(filterWith: string, id: string, cartData: Cart): ProductInfo[] {
    const products = this.createProductInfoForCart(cartData);
    var item = products.filter((item) => {
      return item.productInfo[filterWith] === id;
    });
    return item;
  }

  /**
   * method to trigger cartAdd, cartRemove event for PDL
   * @returns void
   */
  public triggerCartEvent(
    cart?: Cart,
    pdlPageComponentId?: string,
    isCartEmpty?: boolean,
    action?: string
  ): void {
    this.updateCartForPDL(cart, isCartEmpty);
    this.pdlService.triggerCustomEvent(pdlPageComponentId, action);
  }

  /**
   * method to get Service Selected/Unselected for Product Selector tagging
   * @returns ProductInfo[]
   */
  public getSelectedServiceInfo(isSelected: boolean, selectedServiceId: string): ProductInfo[] {
    const cart = this.stateStoreUtilsService.getCart();
    if (!isSelected) {
      return this.getRemovedItemDetails('id', selectedServiceId, cart);
    } else {
      return this.fetchProductInfo(cart, selectedServiceId);
    }
  }

  /**
   * Set type for product in products array and cart.products[]
   * @param productSubType
   * @returns string
   */
  private getType(productSubType: string): string {
    const typeConstants = this.stateStoreUtilsService.getPdlTypeConstants();
    return typeConstants ? this.objectService.get(typeConstants, productSubType, '') : '';
  }

  /**
   * get LineCount for PDL
   * @param cart
   * @returns string
   */
  private getLineCount(product: ProductInfo[]): string {
    let lineCount = '0';
    const unique = [...Array.from(new Set(product.map((item) => item.productInfo.lineNumber)))];
    if (unique.length > 0) {
      if (!unique.includes('')) {
        lineCount = unique.length.toString();
      } else {
        lineCount = (unique.length - 1).toString();
      }
    }
    return lineCount;
  }

  public getPromotionsDetails(promotions: Promotion[], promoCode: string): Promotion[] {
    let promotion = promotions.filter((promotion) => promotion.promoCode === promoCode);
    return promotion ? promotion : [];
  }

  /**
   * get LineCount for PDL
   * @returns ClickToCallEventDetails
   */

  public getTriggerClickToCallEventData(
    tfn: string,
    ctaName?: string,
    href?: string
  ): ClickToCallEventDetails {
    let eventData: ClickToCallEventDetails = {
      eventInfo: {
        eventAction: PDL_EVENT_ACTION.CLICK,
        eventType: PDL_EVENT_TYPE.CLICK_TO_CALL,
      },
    };
    if (ctaName) {
      this.objectService.set(eventData, 'eventDescription.ctaName', ctaName);
      this.objectService.set(eventData, 'eventDescription.targetUrl', href);
      this.objectService.set(eventData, 'eventDescription.assetType', PDL_ASSET_TYPE.TEL);
      this.objectService.set(eventData, 'attributes.tfn', tfn);
    } else {
      this.objectService.set(eventData, 'eventDescription.assetType', PDL_ASSET_TYPE.LINK);
      this.objectService.set(eventData, 'eventDescription.tfn', tfn);
    }
    return eventData;
  }
}
